import {
  faCloudArrowUp,
  faTimes,
  faUpload,
} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';

import {thumbnailMimeTypes} from '../../functions/formatThumbnailBody';
import {Icon} from '../Icon';
import {ProtectedImage} from '../Protected';

import {getAcceptLabels, getAcceptTypes} from './functions';

import type {SingleFileInputValue} from '../../functions/formatThumbnailBody';

type SingleFileInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> & {
  name: string;
  value: SingleFileInputValue;
  onChange: (value: SingleFileInputValue) => void;
  label?: React.ReactNode;
  error?: string;
};
export function SingleFileInput({
  value,
  error,
  onChange,
  className,
  label,
  ...props
}: SingleFileInputProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const accept = props?.accept || thumbnailMimeTypes.join(',');
  const [shortLabel] = getAcceptLabels(accept);

  const {getRootProps, getInputProps, open} = useDropzone({
    accept: getAcceptTypes(accept),
    multiple: false,
    onDrop: (files) => {
      if (files.length === 0) return;
      const file = files[0];

      // should create thumbnail url for image files
      const displayUrl = file.type.includes('image/')
        ? URL.createObjectURL(file)
        : '';

      onChange({
        file,
        displayUrl,
        hasInitialValue: value.hasInitialValue,
        isCleared: value.isCleared,
      });
      setIsHovered(false);
    },
    onFileDialogCancel: () => onChange(value),
    onDragEnter: () => setIsHovered(true),
    onDragLeave: () => setIsHovered(false),
  });

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      setIsHovered(false);
      open();
    },
    [open, setIsHovered]
  );
  const handleClearValue = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      onChange({
        file: null,
        displayUrl: '',
        hasInitialValue: value.hasInitialValue,
        isCleared: true,
      });
    },
    [onChange, value.hasInitialValue]
  );

  // Cleanup local memory
  useEffect(() => {
    return () => {
      value.displayUrl && URL.revokeObjectURL(value.displayUrl);
    };
  }, [value.displayUrl]);

  return (
    <div>
      {label && (
        <label
          htmlFor={props.name}
          className="mb-2 block text-sm/6 font-medium text-slate12"
        >
          {props.required && <span className="mr-1 text-tomato11">&#42;</span>}
          {label}
        </label>
      )}

      <div
        {...getRootProps()}
        className={clsx(
          'max-h-72',
          className,
          'group hocus:border-slate8 flex cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate7 p-2',
          isHovered && 'border-slate8!',
          value.displayUrl &&
            'relative after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:rounded-lg after:border-8 after:border-solid after:border-slate1'
        )}
      >
        <input
          id={props.name}
          className="sr-only"
          {...props}
          {...getInputProps()}
        />

        {value.displayUrl || value.file ? (
          value.displayUrl ? (
            <>
              {value.displayUrl.includes('blob') ? (
                <img
                  src={value.displayUrl}
                  alt="thumbnail"
                  className="object-cover object-center group-hover:brightness-75"
                />
              ) : (
                <ProtectedImage
                  src={value.displayUrl}
                  alt="thumbnail"
                  className="object-cover object-center group-hover:brightness-75"
                />
              )}
              <div className="absolute-center hidden gap-2 group-hover:flex">
                {/* TODO: IconButton */}
                <button
                  type="button"
                  onClick={handleOpen}
                  className="hocus:bg-blue10 rounded-full bg-blue9 p-2.5 leading-none text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue8 focus-visible:outline-solid"
                  data-ignore-dropzone
                >
                  <span className="sr-only">Open dialog</span>
                  <Icon icon={faUpload} size="1x" className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={handleClearValue}
                  className="hocus:bg-tomato10 rounded-full bg-tomato9 p-2.5 leading-none text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tomato8 focus-visible:outline-solid"
                  data-ignore-dropzone
                >
                  <span className="sr-only">Clear file</span>
                  <Icon icon={faTimes} size="1x" className="size-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="w-full bg-blue2 px-6 py-10 text-center">
              <p className="text-base font-medium text-slate12">
                A file has been added
              </p>
              <p className="text-sm text-slate11">No thumbnail is available</p>
            </div>
          )
        ) : (
          <div className="mx-6 my-10 text-center">
            <Icon
              icon={faCloudArrowUp}
              className={clsx(
                'mx-auto size-12 text-slate7 transition-colors group-hover:text-blue11',
                isHovered && 'text-blue11!'
              )}
            />
            <p className="mt-4 text-sm/6 text-slate11">
              <span
                className={clsx(
                  'relative rounded-md bg-slate1 font-semibold text-blue11 group-hover:text-blue10',
                  isHovered && 'text-blue10!'
                )}
              >
                Upload a file
              </span>{' '}
              or drag and drop
            </p>
            <p className="text-xs/5 text-slate11">{shortLabel} accepted</p>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm font-medium text-tomato11">{error}</p>
      )}
    </div>
  );
}
